import * as React from "react";
import { connect } from "react-redux";

import { OverviewScreenStyle } from "./overview-screen-style";
import { RouteComponentProps, useLocation } from "react-router-dom";
import NavBreadcrumb from "../breadcrumb/nav-breadcrumb";
import { RootState, OrganizationsState, TileDescriptionsState, getAppOwner } from "../../../../store/types";
import {
    UserDataAndState,
    Organization,
    getAppEminenceFlags,
    getUserFlags,
    isUserData,
    getUserFeatures,
} from "common/Database";
import {
    isUserOrgID,
    BuilderTileDescription,
    toTileDescription,
    openApp,
    renameOrganization,
    makeNavBreadcrumbItems,
    setAppIsFavorite,
} from "../orgs-lib";
import AppIcon from "common/app-icon";
import * as actions from "../../../../actions/actions";
import * as appsActions from "../../../../actions/apps-actions";
import SerializedAppTile from "../app-tile/serialized-app-tile";
import { LoadingIndicator } from "../orgs-styles";
import AppTile from "../app-tile/app-tile";
import { BuilderTheme } from "sharedUI/styles";
import { withTheme } from "styled-components";
import { MappedThunk } from "../../../../actions/action-thunk";
import TemplateAppTile from "../app-tile/template-app-tile";
import { getFirstRunTemplateSpecs, getProTrialTemplateSpecs, TemplateTileSpec } from "common/first-run-builder";
import { getLocationSettings } from "lib/location";
import { dontAwait } from "common/Support";
import OverviewScreenSection, { SortMode } from "./overview-screen-section";
import { InviteMemberButton } from "designer/apps/ui/smart/invite-member/invite-member-button";
import { trackEvent } from "analytics";
import { NewAppSurveyData } from "common/firebase-function-types";
import NewAppModal from "designer/modals-v2/new-app/new-app-modal";
import { useModals } from "designer/modals-v2/modal-provider-v2/modal-provider-v2";
import DataRequestModal from "designer/modals/smart/data-request-modal/data-request-modal";
import WindowedModalV2 from "designer/modals-v2/windowed-modal/windowed-modal";
import { useLocalStorage } from "hooks/use-local-storage";
import { compareStringsSmartly } from "common/strings";
import { proveNever } from "@glideapps/ts-necessities";
import { GlideIcon } from "sharedUI/ui/views/glide-icon/glide-icon";
import { LearningCenter } from "designer/apps/ui/smart/learning-center/learning-center";
import { AppKind } from "common/components/SerializedApp";
import { SearchField } from "../search-field/search-field";
import "twin.macro";

interface RouteProps {
    readonly orgID: string | undefined;
}

interface Props extends RouteComponentProps<RouteProps> {
    readonly organizations: OrganizationsState;
    readonly userData: UserDataAndState;
    readonly tiles: TileDescriptionsState;
    readonly loadTileDescriptionFromFirebase: MappedThunk<typeof actions.loadTileDescriptionFromFirebase>;
    readonly unloadApps: typeof appsActions.unloadApps;
    readonly onTransferAppToOrganization: (appID: string, org: Organization) => void;
    readonly onTransferAppToUser: (appID: string, withCopy: boolean) => void;
    readonly onNewApp: (
        org: Organization | undefined,
        appName: string | undefined,
        newAppSurveyData: NewAppSurveyData,
        appKind: AppKind
    ) => void;
    readonly onNewGlideTableApp: (
        org: Organization | undefined,
        newApp: string | undefined,
        newAppSurveyData: NewAppSurveyData,
        appKind: AppKind
    ) => void;
    readonly onDeleteApp: (appID: string, title: string) => void;
    readonly theme: BuilderTheme;
    readonly onDuplicateApp: (appID: string, organizationID: string | undefined) => void;
    readonly onMakeSupportCode: (appID: string) => void;
    readonly onNewUser: (joinOrg?: boolean) => Promise<void>;
}

const OverviewScreenImpl: React.FunctionComponent<Props> = p => {
    const {
        organizations,
        match,
        userData,
        tiles,
        loadTileDescriptionFromFirebase,
        history,
        unloadApps,
        onDeleteApp,
        onDuplicateApp,
        onMakeSupportCode,
        onNewApp,
        onNewGlideTableApp,
        onTransferAppToOrganization,
        onTransferAppToUser,
        onNewUser,
        theme,
    } = p;
    const orgID = match.params.orgID;
    const org = organizations.find(o => o.id === orgID);

    const [search, setSearch] = React.useState("");
    const [showLearningCenter, setShowLearningCenter] = React.useState(false);

    const shouldShowRights = match.path.endsWith("account/rights");

    const { showModal } = useModals();

    React.useEffect(() => {
        if (shouldShowRights) {
            const DATA_REQUESTS: string[] = [
                "Access",
                "Deletion / Erasure",
                "Rectification",
                "Withdrawal of Consent",
                "Portability",
                "Objection / Opt-Out",
                "Restriciton of Processing",
            ];

            dontAwait(
                showModal<void>(resolve => {
                    return (
                        <WindowedModalV2 canClose={true} onClose={resolve} hideClosingButton={true}>
                            <DataRequestModal
                                header="Data Request"
                                choices={DATA_REQUESTS}
                                onAccept={resolve}
                                onCancel={() => resolve(undefined)}
                            />
                        </WindowedModalV2>
                    );
                })
            );
        }
    }, [shouldShowRights, showModal]);

    const onLearnClicked = React.useCallback(() => {
        setShowLearningCenter(true);
    }, []);

    const onLearningCenterClosed = React.useCallback(() => {
        setShowLearningCenter(false);
    }, []);

    const openAppImpl = React.useCallback(
        (appID: string) => {
            dontAwait(openApp(appID, userData, history, unloadApps));
        },
        [history, unloadApps, userData]
    );

    const createAppTile = (
        isSample: boolean,
        r: BuilderTileDescription,
        organizationID: string | undefined,
        index: number
    ): React.ReactNode => {
        const isOrg = organizationID !== undefined && organizationID !== "0";
        return (
            <SerializedAppTile
                app={r}
                appID={r.appID}
                userData={userData}
                organizations={organizations}
                isSample={isSample}
                key={r.appID}
                isSelected={!isSample && index === 0 && search !== ""}
                onDeleteApp={onDeleteApp}
                onDuplicateApp={appID => onDuplicateApp(appID, isOrg ? organizationID : undefined)}
                onTransferToOrganization={onTransferAppToOrganization}
                onTransferToUser={onTransferAppToUser}
                onMakeSupportCode={onMakeSupportCode}
                isFavorite={r.isFavorite}
                setIsFavorite={
                    isSample || !isOrg
                        ? undefined
                        : async isFavorite => {
                              if (organizationID !== undefined) {
                                  await setAppIsFavorite(organizationID, r.appID, isFavorite);
                              }
                          }
                }
            />
        );
    };

    const createTemplateTile = (ts: TemplateTileSpec) => {
        return (
            <TemplateAppTile
                key={`${ts.name}-templateAppTile`}
                isProTrial={ts.privateProTrial}
                name={ts.name}
                templateID={ts.templateID}
                color={ts.color}
                icon={ts.icon}
                targetOwnerID={ts.targetOwnerID}
            />
        );
    };

    const searchFilter = React.useCallback(
        (tile: BuilderTileDescription): boolean => {
            const searchLower = search.toLowerCase();
            let eminenceBadge: string | undefined;
            const owner = getAppOwner(tile.appID, userData, organizations);
            if (owner !== undefined) {
                eminenceBadge = getAppEminenceFlags(owner, tile.appID, undefined).badge;
            }
            return (
                tile.title === undefined ||
                tile.title.toLowerCase().includes(searchLower) ||
                (eminenceBadge !== undefined && eminenceBadge.toLowerCase().startsWith(searchLower))
            );
        },
        [search, userData, organizations]
    );

    const getAppIDs = React.useCallback(
        (o: Organization | undefined): readonly string[] | undefined => {
            let appIDs: readonly string[] | undefined;
            if (o === undefined && userData !== null && userData !== "loading") {
                appIDs = userData.appIDs;
            } else if (o !== undefined) {
                appIDs = o.appIDs;
            }
            return appIDs;
        },
        [userData]
    );

    const onSearchEnter = React.useCallback(() => {
        const appIDs = getAppIDs(org);
        if (appIDs !== undefined) {
            if (!isUserData(userData)) return;
            const filtered = appIDs
                .map(a => toTileDescription(a, userData.id, tiles, loadTileDescriptionFromFirebase))
                .filter(searchFilter);

            if (filtered.length > 0) {
                openAppImpl(filtered[0].appID);
            }
        }
    }, [getAppIDs, loadTileDescriptionFromFirebase, openAppImpl, org, searchFilter, tiles, userData]);

    const [showNewAppModal, setShowNewAppModal] = React.useState(false);
    const [showNewPageModal, setShowNewPageModal] = React.useState(false);

    const location = useLocation();
    React.useEffect(() => {
        if (location.pathname.endsWith("/newApp")) {
            history.replace(location.pathname.substring(0, location.pathname.length - "/newApp".length));
            setShowNewAppModal(true);
        }
    }, [history, location]);

    let modal: React.ReactNode;
    if (showNewAppModal && isUserData(userData)) {
        modal = (
            <NewAppModal
                onNewApp={(a, b, c) => onNewApp(a, b, c, AppKind.App)}
                onNewGlideTableApp={(a, b, c) => onNewGlideTableApp(a, b, c, AppKind.App)}
                onModalCancel={() => setShowNewAppModal(false)}
                org={org}
                userData={userData}
            />
        );
    } else if (showNewPageModal && isUserData(userData)) {
        modal = (
            <NewAppModal
                onNewApp={(a, b, c) => onNewApp(a, b, c, AppKind.Page)}
                onNewGlideTableApp={(a, b, c) => onNewGlideTableApp(a, b, c, AppKind.Page)}
                onModalCancel={() => setShowNewPageModal(false)}
                org={org}
                userData={userData}
            />
        );
    }

    React.useEffect(() => {
        const joinOrg = match.url.endsWith("joined");
        if (isUserData(userData) && joinOrg) {
            // Team invite
            dontAwait(onNewUser(true));
        } else if (
            org !== undefined &&
            isUserData(userData) &&
            ((userData.appIDs.length === 0 &&
                userData.orgUserIDs.length === 1 &&
                org.memberUserIDs.length === 1 &&
                !getUserFlags(userData).onboardingComplete) ||
                getUserFlags(userData).lastAcceptedTermsOfService === 0)
        ) {
            // First user run (thats not from a team invite)
            dontAwait(onNewUser());
        }
    }, [org, userData, onNewUser, match.url]);

    const [sortMode, setSortMode] = useLocalStorage(`machine-sort-mode-${orgID ?? "0"}`, SortMode.OldestFirst);
    const onSort = React.useCallback(
        (newSortMode: SortMode) => {
            setSortMode(newSortMode);
        },
        [setSortMode]
    );

    if (org === undefined && !isUserOrgID(orgID)) {
        return (
            <OverviewScreenStyle>
                <div className="OverviewScreenImpl">
                    <LoadingIndicator />
                </div>
            </OverviewScreenStyle>
        );
    }

    const appIDs = getAppIDs(org);

    let appsNode: React.ReactNode[] = [];
    let favoritesNodes: React.ReactNode[] = [];
    if (isUserData(userData) && appIDs !== undefined && appIDs.length > 0) {
        const tileDescriptions = appIDs
            .map(a => toTileDescription(a, org?.id ?? userData.id, tiles, loadTileDescriptionFromFirebase))
            .filter(searchFilter);
        let appsTiles = tileDescriptions.filter(t => t.isFavorite !== true);

        switch (sortMode) {
            case SortMode.OldestFirst:
                // This is the default order
                break;
            case SortMode.NewestFirst:
                appsTiles.reverse();
                break;
            case SortMode.Alphabetical:
                appsTiles = appsTiles.sort((a, b) => compareStringsSmartly(a.title, b.title));
                break;
            default:
                // We broke backwards compatiblity with `SortMode`, so we
                // can't `assertNever`.  We just default to the `OldestFirst`.
                // https://github.com/quicktype/glide/pull/9859
                proveNever(sortMode, "This is fine", undefined);
        }

        appsNode = appsTiles.map((e, i) => createAppTile(false, e, orgID, i));
        favoritesNodes = tileDescriptions
            .filter(t => t.isFavorite === true)
            .map((e, i) => createAppTile(false, e, orgID, i));
    }

    const { useProductionTemplateIDs } = getLocationSettings();
    const firstRunTemplateSpecs = getFirstRunTemplateSpecs(useProductionTemplateIDs);

    const sampleTemplateNodes: React.ReactNode[] = firstRunTemplateSpecs
        .filter(ts => ts.name.toLowerCase().includes(search))
        .map(ts => createTemplateTile({ ...ts, targetOwnerID: orgID }));

    const proTrials = getProTrialTemplateSpecs(useProductionTemplateIDs)
        .filter(ts => ts.name.toLowerCase().includes(search))
        .map(ts => createTemplateTile({ ...ts, targetOwnerID: orgID }));

    const { glidePages } = getUserFeatures(userData);

    const newAppNode = (
        <AppTile
            dimBackground={true}
            accentColor="#FFFFFF"
            name="New App"
            onClick={() => {
                setShowNewAppModal(true);
                trackEvent("new app", {});
            }}>
            <div className="new-app-icon">
                <AppIcon icon={"plusStroke"} size={24} />
            </div>
        </AppTile>
    );

    const newPageNode = (
        <AppTile
            dimBackground={true}
            accentColor="#FFFFFF"
            name="New Page"
            onClick={() => {
                setShowNewPageModal(true);
                trackEvent("new page", {});
            }}>
            <div className="new-app-icon">
                <AppIcon icon={"plusStroke"} size={24} />
            </div>
        </AppTile>
    );

    return (
        <OverviewScreenStyle>
            {modal}
            <LearningCenter show={showLearningCenter} onClose={onLearningCenterClosed} />
            <div className="ovsc-header-wrap">
                <div className="ovsc-header-wrap">
                    <NavBreadcrumb
                        items={makeNavBreadcrumbItems(undefined, org?.id, organizations)}
                        onItemRename={
                            org === undefined
                                ? undefined
                                : (_, newName) => {
                                      if (org !== undefined) {
                                          void renameOrganization(org.id, newName);
                                      }
                                  }
                        }
                    />
                    {org !== undefined && userData !== null && userData !== "loading" && (
                        <InviteMemberButton source={"overview_screen"} organization={org} userID={userData.id} />
                    )}
                </div>
                <div className="header-right">
                    <SearchField
                        value={search}
                        placeholder="Type to search apps…"
                        onChange={setSearch}
                        onEnter={onSearchEnter}
                        variant="minimal"
                    />
                    <button className="button-link" onClick={onLearnClicked}>
                        <GlideIcon
                            strokeColor={theme.iconPale}
                            strokeWidth={1.5}
                            kind="stroke"
                            icon="st-book"
                            iconSize={20}
                        />
                        <div className="title">Learn</div>
                    </button>
                    <a
                        className="button-link"
                        href="https://community.glideapps.com/"
                        target="_blank"
                        rel="noopener noreferrer">
                        <GlideIcon
                            strokeColor={theme.iconPale}
                            strokeWidth={1.5}
                            kind="stroke"
                            icon="st-people"
                            iconSize={20}
                        />
                        <div className="title">Community</div>
                    </a>
                </div>
            </div>

            {favoritesNodes.length > 0 && (
                <OverviewScreenSection title="Starred">
                    {newAppNode}
                    {glidePages && newPageNode}
                    {favoritesNodes}
                </OverviewScreenSection>
            )}

            {(appsNode.length > 0 || favoritesNodes.length === 0) && (
                <OverviewScreenSection title="Apps" onSort={onSort} currentSortMode={sortMode}>
                    {favoritesNodes.length === 0 && newAppNode}
                    {favoritesNodes.length === 0 && glidePages && newPageNode}
                    {appsNode}
                </OverviewScreenSection>
            )}

            {proTrials.length > 0 && (
                <OverviewScreenSection
                    title="Try Pro Apps for Work"
                    subtitle={
                        <span>
                            Get a 14-day free trial of a private pro app for your team.{" "}
                            <a href="https://www.glideapps.com/for-work">Learn More</a>
                        </span>
                    }
                    button={{
                        label: "View All",
                        onClick() {
                            window.open("https://www.glideapps.com/for-work", "_blank");
                        },
                    }}>
                    {proTrials}
                </OverviewScreenSection>
            )}

            {sampleTemplateNodes.length > 0 && (
                <OverviewScreenSection
                    title={"Start from a classic template"}
                    button={{
                        label: "View All",
                        onClick() {
                            window.open("https://www.glideapps.com/templates", "_blank");
                        },
                    }}>
                    {sampleTemplateNodes}
                </OverviewScreenSection>
            )}
        </OverviewScreenStyle>
    );
};

const mapState = (state: RootState) => ({
    organizations: state.organizations,
    userData: state.userData,
    tiles: state.tiles,
});

const mapDispatch = {
    loadTileDescriptionFromFirebase: actions.loadTileDescriptionFromFirebase,
    unloadApps: appsActions.unloadApps,
};

const OverviewScreen = withTheme(connect(mapState, mapDispatch)(OverviewScreenImpl));
export default OverviewScreen;
